import React from "react";
import { Waveform } from "@phosphor-icons/react";
import { isRTL, langLabel } from "@/lib/languages";

/**
 * One-row focus card showing only the currently-playing segment with its
 * translation alongside. Renders just below the audio player so the user
 * always sees what's happening *right now* without scanning the long
 * scrollable viewers below.
 */
export default function NowPlayingRow({
  segments = [],
  translation = null,
  currentTime = 0,
  sourceLanguage,
  sourceDialect,
}) {
  const activeIdx = (() => {
    if (!segments.length) return -1;
    for (let i = 0; i < segments.length; i++) {
      const s = segments[i];
      if (currentTime >= (s.start || 0) && currentTime < (s.end || 0)) return i;
    }
    return -1;
  })();

  const seg = activeIdx >= 0 ? segments[activeIdx] : null;
  const tSeg = (translation?.segments || [])[activeIdx] || null;
  const tLang = translation?.target_language || sourceLanguage;
  const tDialect = translation?.target_dialect;
  const tTitle = translation
    ? (translation.mode === "dialect" ? "DIALECT" : "TRANSLATION")
    : "TRANSLATION";

  return (
    <div className="hard-border bg-white" data-testid="now-playing-row">
      <div className="px-5 py-2 border-b border-[--line] bg-[--paper] flex items-center justify-between">
        <div className="overline text-[--muted] inline-flex items-center gap-2">
          <Waveform weight="bold" size={14} /> NOW PLAYING
        </div>
        <div className="font-mono text-[10px] text-[--muted] tabular-nums">
          {seg
            ? `${fmtClock(seg.start)} – ${fmtClock(seg.end)} · #${activeIdx + 1}/${segments.length}`
            : "—"}
        </div>
      </div>
      <div className="grid lg:grid-cols-2 divide-x divide-[--line]">
        <FocusCell
          title="ORIGINAL"
          language={sourceLanguage}
          dialect={sourceDialect}
          text={seg?.text || ""}
          placeholder="Press play to follow along…"
        />
        <FocusCell
          title={tTitle}
          language={tLang}
          dialect={tDialect}
          text={tSeg?.text || ""}
          placeholder={translation ? "Press play to follow along…" : "No translation yet"}
          inverse
        />
      </div>
    </div>
  );
}

function FocusCell({ title, language, dialect, text, placeholder, inverse }) {
  const rtl = isRTL(language);
  const hasText = !!(text || "").trim();
  return (
    <div className={`px-5 py-4 ${inverse ? "bg-[#0f0f10] text-white" : "bg-white"}`}>
      <div className={`flex items-center justify-between mb-2 ${inverse ? "text-white/70" : "text-[--muted]"}`}>
        <div className="overline">{title}</div>
        <div className="font-mono text-[10px]">
          {langLabel(language)}{dialect ? ` · ${dialect}` : ""}
        </div>
      </div>
      <div
        className={`text-lg leading-snug font-display ${hasText ? "" : `italic ${inverse ? "text-white/30" : "text-[--muted]"}`}`}
        dir={rtl ? "rtl" : "ltr"}
        data-testid={`now-playing-${title.toLowerCase()}`}
      >
        {hasText ? text : placeholder}
      </div>
    </div>
  );
}

function fmtClock(sec) {
  if (sec == null) return "00:00";
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}
